import { useTranslation } from 'react-i18next';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';

import { FEATURE_FLAGS, usePartnerFeatureFlag } from 'support/helpers/featureFlags/featureFlags';
import { DocumentType, DocumentTypeWithVirtual, RelationshipV2 } from 'support/types';
import { Button } from 'components/Form/Button';
import { Page } from 'components/Page/Page';
import { OcrResult } from 'components/WebEDI/DocumentExtractor/types';
import { PriceList } from 'components/WebEDI/PriceList/PriceList';

import { CreateDocumentSteps } from '../../hooks/useCreateDocumentSteps';
import { OCRButton } from '../OCRButton/OCRButton';

export const CreateDocumentHeader = ({
  relationship,
  nonVirtualDocumentType,
  virtualDocumentType,
  sourceMessageId,
  step,
  handleOpenConfirmation,
  isCreatingDocument,
  isPreppingDocumentForSubmission,
  setOcrResult,
  onCancel,
}: {
  step: CreateDocumentSteps;
  relationship: RelationshipV2 | undefined;
  virtualDocumentType: DocumentTypeWithVirtual;
  nonVirtualDocumentType: DocumentType;
  sourceMessageId?: string;
  handleOpenConfirmation: () => void;
  isCreatingDocument: boolean;
  isPreppingDocumentForSubmission: boolean;
  setOcrResult: (result: OcrResult | undefined) => void;
  onCancel: () => void;
}) => {
  const { t } = useTranslation();
  const { enabled: priceCatalogsEnabled } = usePartnerFeatureFlag(FEATURE_FLAGS.TEMPORARY_PRICE_CATALOGS);

  return (
    <>
      <Page.Head
        rightContent={
          <div className="flex items-center gap-2">
            <Button
              variant="minimal"
              disabled={isCreatingDocument || isPreppingDocumentForSubmission}
              onClick={onCancel}
              analyticsId="webedi:new_document_cancel"
            >
              {t('webedi:createDocument.cancel')}
            </Button>
            {[DocumentTypeWithVirtual.invoice, DocumentTypeWithVirtual.creditNote].includes(virtualDocumentType) &&
              !priceCatalogsEnabled && <PriceList receiverPartnerId={relationship?.receiver_partner?.id} />}
            <OCRButton
              setOcrResult={setOcrResult}
              targetRelationship={relationship}
              sourceMessageId={sourceMessageId}
              documentType={nonVirtualDocumentType}
              virtualDocumentType={virtualDocumentType}
              disabled={isCreatingDocument || isPreppingDocumentForSubmission}
            />
            <Button
              type="button"
              onClick={handleOpenConfirmation}
              disabled={
                step !== CreateDocumentSteps.DOCUMENT_CREATION || isCreatingDocument || isPreppingDocumentForSubmission
              }
              loading={isCreatingDocument || isPreppingDocumentForSubmission}
              RightIcon={PaperAirplaneIcon}
              analyticsId="webedi:new_document_send"
            >
              {t('webedi:createDocument.submit')}
            </Button>
          </div>
        }
      />
    </>
  );
};
